import React, { useId } from 'react';
import classNames from 'classnames';
import _ from 'lodash';
import { LayoutGroup, motion, MotionConfig } from 'framer-motion';
import * as styles from './ReactionAvatarStack.module.scss';
import ReactionAvatar from './ReactionAvatar';
import useAppSelector from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { useIsSize } from '../../utils/responsive/isSize';
import Sizes from '../../utils/responsive/Sizes';

const ABSOLUTE_MAXIMUM = 80;

const REACTION_FRAGMENT = gql(`
  fragment ReactionAvatarStack_reaction on PublicReactionFields {
    id
    insertedAt
    ...ReactionAvatar_reaction
  }
`);

type ReactionFragment = DocumentType<typeof REACTION_FRAGMENT>;

interface ReactionAvatarStackItemContainerProps {
  children: React.ReactNode;
  className?: string;
}

export const ReactionAvatarStackItemContainer = ({
  children,
  className,
}: ReactionAvatarStackItemContainerProps) => {
  return (
    <div className={classNames(styles.avatarStackItemContainer, className)}>
      {children}
    </div>
  );
};

interface AvatarContainerProps {
  children: React.ReactNode;
  last: boolean;
  index: number;
  total: number;
  layoutId: string;
  disableLayoutAnimation?: boolean;
}

const AvatarContainer = ({
  children,
  last,
  index,
  total,
  layoutId,
  disableLayoutAnimation,
}: AvatarContainerProps) => {
  return (
    <motion.div
      {...(disableLayoutAnimation
        ? {}
        : {
            layout: 'position',
            layoutId,
          })}
      className={classNames(styles.avatarContainer, {
        [styles.last]: last,
      })}
      initial={{
        // We need to set the transform directly including the initial translate position
        // as otherwise, it would animate the AvatarContainer in from the left
        transform: 'translate3d(0px,0px,0px) scale(0.6,0.6)',
      }}
      animate={{
        transform: 'translate3d(0px,0px,0px) scale(1,1)',
      }}
      style={{
        zIndex: total - index,
      }}
    >
      {children}
    </motion.div>
  );
};

interface ReactionAvatarStackProps {
  reactions: ReactionFragment[];
  ring?: boolean;
  withinOverlay?: boolean;
  maxItemsShown?: number;
  additionalNode?: React.ReactNode;
  onEmojiClick?: (reaction: ReactionFragment, event: React.MouseEvent) => void;
  spacing?: 'tight' | 'loose';
  showAdditionalNode?: boolean;
}

const ReactionAvatarStack = ({
  reactions,
  ring,
  withinOverlay,
  additionalNode,
  onEmojiClick,
  spacing = 'tight',
  maxItemsShown,
  showAdditionalNode,
}: ReactionAvatarStackProps) => {
  const stackId = useId();
  const isMobile = useIsSize(Sizes.SM);
  const shouldOverlapAdditionalNode = maxItemsShown === 3;
  const currentUser = useAppSelector((state) => state.session.user);

  const emojiPopularity = _.sortBy(_.entries(_.countBy(reactions, 'emoji')), 1)
    .toReversed()
    .map(([emoji]) => emoji);

  const sortedReactions = _.sortBy(reactions, (reaction) => [
    new Date(reaction.insertedAt).valueOf(),
    emojiPopularity.length - emojiPopularity.indexOf(reaction.emoji),
    reaction.author.id === currentUser.id ? 1 : 0,
    reaction.author.id,
  ]).toReversed();

  const slicedReactions = (() => {
    if (
      maxItemsShown != null &&
      sortedReactions.length >= maxItemsShown &&
      maxItemsShown > 1
    ) {
      return sortedReactions.slice(
        0,
        maxItemsShown === 3 ? maxItemsShown - 1 : maxItemsShown - 2,
      );
    }

    if (maxItemsShown === 1) {
      return sortedReactions.slice(0, 1);
    }

    return sortedReactions.slice(0, ABSOLUTE_MAXIMUM);
  })();

  const overflowCount = reactions.length - (slicedReactions.length - 1);

  return (
    <LayoutGroup id={stackId}>
      <MotionConfig transition={{ duration: 0.1 }}>
        <div
          className={classNames(styles.container, {
            [styles.wrap]: maxItemsShown == null,
            [styles.withinOverlay]: withinOverlay,
            [styles.overlapAdditionalNode]: shouldOverlapAdditionalNode,
            [styles.loose]: spacing === 'loose',
          })}
        >
          {slicedReactions.map((reaction, i) => {
            const isLast = i === slicedReactions.length - 1;
            const isFromCurrentUser = reaction.author.id === currentUser.id;
            const avatar = (
              <ReactionAvatar
                ring={ring}
                reaction={reaction}
                active={isFromCurrentUser}
                withinOverlay={withinOverlay}
              />
            );

            return (
              <AvatarContainer
                disableLayoutAnimation={isMobile}
                layoutId={reaction.id}
                last={isLast}
                index={i}
                total={slicedReactions.length}
                key={reaction.id}
              >
                {onEmojiClick ? (
                  <Button onClick={(event) => onEmojiClick(reaction, event)}>
                    {avatar}
                  </Button>
                ) : (
                  avatar
                )}
              </AvatarContainer>
            );
          })}

          {(maxItemsShown == null || maxItemsShown > 1) && (
            <div>
              {overflowCount > 1 && !showAdditionalNode ? (
                <ReactionAvatarStackItemContainer className={styles.counter}>
                  {reactions.length}
                </ReactionAvatarStackItemContainer>
              ) : (
                <div
                  className={classNames(styles.additionalNode, {
                    [styles.visible]: showAdditionalNode,
                  })}
                >
                  {additionalNode}
                </div>
              )}
            </div>
          )}
        </div>
      </MotionConfig>
    </LayoutGroup>
  );
};

export default ReactionAvatarStack;
